import { useState, useEffect, useMemo, useRef, useId } from "react";
import axios from "axios";
import { addCustomLabTest, getAllLabTests, loadLabTestCatalog } from "../data/labTests";
import { ChevronDown, Plus, X } from "lucide-react";
import DropdownPanel from "./ui/DropdownPanel";

const TestNameAutocomplete = ({ value, onChange, onSelectTest, inputId, labTests }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const listboxId = useId();

  const matches = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    const list = labTests || [];
    if (!q) return list.slice(0, 50);
    const starts = [];
    const contains = [];
    for (const t of list) {
      const n = t.name.toLowerCase();
      if (n.startsWith(q)) starts.push(t);
      else if (n.includes(q)) contains.push(t);
    }
    return [...starts, ...contains].slice(0, 50);
  }, [value, labTests]);

  useEffect(() => {
    setHighlight(0);
  }, [value, open]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const exactMatch = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return null;
    return (labTests || []).find((t) => t.name.toLowerCase() === q) || null;
  }, [value, labTests]);

  const showCustomOption = !!(value || "").trim() && !exactMatch;

  const commitTest = (test) => {
    onSelectTest(test);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const total = matches.length + (showCustomOption ? 1 : 0);
      if (total === 0) return;
      setHighlight((h) => (h + 1) % total);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const total = matches.length + (showCustomOption ? 1 : 0);
      if (total === 0) return;
      setHighlight((h) => (h - 1 + total) % total);
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      if (highlight < matches.length) {
        commitTest(matches[highlight]);
      } else if (showCustomOption) {
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const renderHighlighted = (name) => {
    const q = (value || "").trim();
    if (!q) return name;
    const idx = name.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return name;
    return (
      <>
        {name.slice(0, idx)}
        <mark className="bg-teal-100 text-teal-900 rounded px-0.5">
          {name.slice(idx, idx + q.length)}
        </mark>
        {name.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          id={inputId}
          type="text"
          name="test"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={value ?? ""}
          onChange={(e) => {
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search test name…"
          className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? "Close test list" : "Open test list"}
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen((v) => !v);
          }}
          className="absolute inset-y-0 right-0 mt-1 mr-1 flex w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      <DropdownPanel
        open={open}
        transformOrigin="top center"
        className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/[0.04]"
      >
        <ul
          id={listboxId}
          role="listbox"
          className="max-h-72 overflow-y-auto py-1 text-sm"
        >
            {matches.length === 0 && !showCustomOption && (
              <li className="px-3 py-2 text-gray-500">No matching tests</li>
            )}
            {matches.map((t, i) => {
              const active = i === highlight;
              return (
                <li
                  key={t.name}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitTest(t);
                  }}
                  className={`flex cursor-pointer items-start justify-between gap-3 px-3 py-2 ${
                    active ? "bg-teal-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900">
                      {renderHighlighted(t.name)}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      Normal range: {t.range || "—"}
                    </div>
                  </div>
                  {t.unit && (
                    <span className="shrink-0 self-center rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700">
                      {t.unit}
                    </span>
                  )}
                </li>
              );
            })}
            {showCustomOption && (
              <li
                role="option"
                aria-selected={highlight === matches.length}
                onMouseEnter={() => setHighlight(matches.length)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpen(false);
                }}
                className={`flex cursor-pointer items-center gap-2 border-t border-gray-100 px-3 py-2 text-gray-700 ${
                  highlight === matches.length ? "bg-teal-50" : "hover:bg-gray-50"
                }`}
              >
                <Plus className="h-4 w-4 text-teal-600" strokeWidth={2} aria-hidden="true" />
                <span className="text-sm">
                  Use <span className="font-semibold text-gray-900">“{value.trim()}”</span> as a custom test
                </span>
              </li>
            )}
          </ul>
      </DropdownPanel>
    </div>
  );
};

const LabResultsSection = ({ pmrn, API_BASE_URL }) => {
  const [labResults, setLabResults] = useState([]);
  const [editingLab, setEditingLab] = useState(null);
  
  const [selectedUnit, setSelectedUnit] = useState("");
  const [labTests, setLabTests] = useState(() => getAllLabTests());
  const [isSavingLab, setIsSavingLab] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadLabTestCatalog().then((list) => {
      if (!cancelled) setLabTests(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const emptyLab = {
    PMRN: pmrn,
    test: "",
    result: "",
    normal_range: "",
    date: "",
  };

  useEffect(() => {
    if (!editingLab?.test) {
      setSelectedUnit("");
      return;
    }
    const match = labTests.find(
      (t) => t.name.toLowerCase() === String(editingLab.test).trim().toLowerCase()
    );
    setSelectedUnit(match?.unit || "");
  }, [editingLab?.test, labTests]);

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    if (!pmrn) return;

    const fetchLabResults = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/lab-results/patient/${pmrn}`);
        setLabResults(res.data);
      } catch (err) {
        console.error("Error fetching lab results:", err);
      }
    };
    fetchLabResults();
  }, [pmrn, API_BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingLab({ ...editingLab, [name]: value });
  };

  const handleAdd = async () => {
    if (!editingLab?.date) {
      alert("Date is required");
      return;
    }
    try {
      setIsSavingLab(true);
      const typedTest = String(editingLab?.test || "").trim();
      if (typedTest) {
        const existsInCatalog = labTests.some(
          (t) => t.name.toLowerCase() === typedTest.toLowerCase()
        );
        if (!existsInCatalog) {
          try {
            const created = await addCustomLabTest({
              name: typedTest,
              unit: selectedUnit || "",
              range: editingLab?.normal_range || "",
            });
            setLabTests((prev) => {
              const withoutDup = prev.filter(
                (t) => t.name.toLowerCase() !== created.name.toLowerCase()
              );
              return [...withoutDup, created].sort((a, b) =>
                a.name.localeCompare(b.name)
              );
            });
          } catch (catalogErr) {
            console.error("Could not persist custom lab test to catalog:", catalogErr);
          }
        }
      }
      const res = await axios.post(`${API_BASE_URL}/lab-results/?pmrn=${pmrn}`, editingLab);
      setLabResults([...labResults, res.data]);
      setEditingLab(null);
    } catch (err) {
      console.error("Error adding lab result:", err.response?.data || err);
      alert("Failed to add lab result: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsSavingLab(false);
    }
  };

  const handleEditClick = (lab) => setEditingLab(lab);

  const handleUpdate = async () => {
    if (!editingLab?.date) {
      alert("Date is required");
      return;
    }
    try {
      setIsSavingLab(true);
      const typedTest = String(editingLab?.test || "").trim();
      if (typedTest) {
        const existsInCatalog = labTests.some(
          (t) => t.name.toLowerCase() === typedTest.toLowerCase()
        );
        if (!existsInCatalog) {
          try {
            const created = await addCustomLabTest({
              name: typedTest,
              unit: selectedUnit || "",
              range: editingLab?.normal_range || "",
            });
            setLabTests((prev) => {
              const withoutDup = prev.filter(
                (t) => t.name.toLowerCase() !== created.name.toLowerCase()
              );
              return [...withoutDup, created].sort((a, b) =>
                a.name.localeCompare(b.name)
              );
            });
          } catch (catalogErr) {
            console.error("Could not persist custom lab test to catalog:", catalogErr);
          }
        }
      }
      const res = await axios.put(`${API_BASE_URL}/lab-results/${editingLab.id}`, editingLab);
      setLabResults(labResults.map(l => (l.id === res.data.id ? res.data : l)));
      setEditingLab(null);
    } catch (err) {
      console.error("Error updating lab result:", err.response?.data || err);
      alert("Failed to update lab result: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsSavingLab(false);
    }
  };

  useEffect(() => {
    if (!editingLab) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") setEditingLab(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [editingLab]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lab result?")) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/lab-results/${id}`);
      setLabResults(labResults.filter(l => l.id !== id));
    } catch (err) {
      console.error("Error deleting lab result:", err.response?.data || err);
      alert("Failed to delete lab result: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Lab Results</h3>
        <button
          onClick={() => setEditingLab({ ...emptyLab })}
          className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-500 text-sm"
        >
          Add New Lab Result
        </button>
      </div>

      {editingLab && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h4 className="text-sm font-semibold text-gray-800">
              {editingLab.id ? "Edit Lab Result" : "Add Lab Result"}
            </h4>
            <button
              type="button"
              onClick={() => setEditingLab(null)}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close lab form"
            >
              <X className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          <div className="p-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="lab-test-name" className="block text-sm font-medium text-gray-700">
                  Test Name
                </label>
                {selectedUnit && (
                  <span
                    className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700"
                    title="Preferred unit from reference catalog"
                  >
                    Units: {selectedUnit}
                  </span>
                )}
              </div>
              <TestNameAutocomplete
                inputId="lab-test-name"
                value={editingLab.test}
                labTests={labTests}
                onChange={(val) => setEditingLab((prev) => ({ ...prev, test: val }))}
                onSelectTest={(t) =>
                  setEditingLab((prev) => ({
                    ...prev,
                    test: t.name,
                    normal_range: t.range || prev.normal_range,
                  }))
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Result</label>
              <input
                type="text"
                name="result"
                value={editingLab.result ?? ""}
                onChange={handleChange}
                placeholder={selectedUnit ? `Value (${selectedUnit})` : "Value"}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Normal Range</label>
              <input
                type="text"
                name="normal_range"
                value={editingLab.normal_range ?? ""}
                onChange={handleChange}
                placeholder="e.g. 70–99"
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="date"
                name="date"
                required
                value={editingLab.date ?? ""}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {editingLab.id ? (
              <>
                <button
                  onClick={handleUpdate}
                  disabled={isSavingLab}
                  className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSavingLab ? "Saving..." : "Update"}
                </button>
                <button onClick={() => setEditingLab(null)} className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400">
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleAdd}
                  disabled={isSavingLab}
                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSavingLab ? "Saving..." : "Add"}
                </button>
                <button onClick={() => setEditingLab(null)} className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400">
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Test</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Result</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Normal Range</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {labResults.map((lab) => (
              <tr key={lab.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{lab.test}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{lab.result}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{lab.normal_range}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(lab.date)}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button
                    onClick={() => handleEditClick(lab)}
                    className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-500 text-xs"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(lab.id)}
                    className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-500 text-xs"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LabResultsSection;
